import {
  useToast,
  Button,
  ButtonProps,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import FileSaver from 'file-saver';
import React, { useCallback, useState } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import {
  InputTypeGQLType,
  ReceiptZipFileDownloadAvailableGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import {
  useGetDeliverableArtifactInstanceDownloadLazyQueryGQLType,
  useGetDeliverablesContainerReceiptZipFilesDownloadUrlLazyQueryGQLType,
} from '../requests/deliverables.gql.generated';
import { IconButton } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons/fontAwesome/Icon';
import {
  successToastConfig,
  errorToastId,
  errorToastConfig,
} from 'packages/common-frontend/constants';

type DownloadButtonProps = ButtonProps & {
  fileName: string;
  artifactInstanceId: string;
  iconOnly?: boolean;
  text?: string;
};
export const DownloadButton = ({
  fileName,
  artifactInstanceId,
  iconOnly,
  text,
  ...buttonProps
}: DownloadButtonProps) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [
    getDeliverableArtifactInstanceDownload,
  ] = useGetDeliverableArtifactInstanceDownloadLazyQueryGQLType({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    fetchPolicy: 'no-cache',
    onCompleted: (response) => {
      if (response && response.deliverableArtifactInstanceDownload) {
        const { deliverableArtifactInstanceDownload } = response;
        deliverableArtifactInstanceDownload.forEach((downloadResponse, idx) => {
          if (downloadResponse.__typename === 'ArtifactInstanceDownloadUrl') {
            const url = downloadResponse.signedUrl;
            axios({
              url,
              method: 'GET',
              responseType: 'blob',
            })
              .then((res) => {
                const fileBlob = new Blob([res.data], {
                  type: res.data.type,
                });
                FileSaver.saveAs(fileBlob, `${fileName}-${idx}`);
                setLoading(false);
              })
              .catch(() => {
                setLoading(true);
                if (!toast.isActive(errorToastId)) {
                  errorToastConfig.title = `Error downloading ${fileName} [${idx}]`;
                  toast(errorToastConfig);
                }
              });
          }
          if (
            downloadResponse.__typename === 'ArtifactInstanceDownloadObject'
          ) {
            const object = downloadResponse.object;
            const blob = new Blob([JSON.stringify(object)], {
              type: 'application/json',
            });
            const url = window.URL.createObjectURL(blob);

            const tag = document.createElement('a');
            tag.setAttribute('href', url);
            tag.setAttribute('download', `${fileName}-${idx}.json`);
            tag.click();
            setLoading(false);
          }
        });
      }
    },
  });

  const onClick = useCallback(() => {
    setLoading(true);
    Sentry.addBreadcrumb({
      category: 'ui.click',
      message: `Clicked download for ${fileName} v4 deliverables [iconOnly: ${
        iconOnly ?? false
      }]`,
      level: Sentry.Severity.Info,
    });
    getDeliverableArtifactInstanceDownload({
      variables: { artifactInstanceId },
    });
  }, [
    fileName,
    iconOnly,
    getDeliverableArtifactInstanceDownload,
    artifactInstanceId,
  ]);

  if (iconOnly) {
    return (
      <IconButton
        icon={<Icon icon="arrow-down-to-bracket" color="blue.400" />}
        isLoading={loading}
        color="blue.400"
        ariaLabel="download-file"
        label="Download File"
        onClick={onClick}
      />
    );
  }

  return (
    <Button
      isLoading={loading}
      color="blue.400"
      leftIcon={<Icon icon="arrow-down-to-bracket" boxSize={4} />}
      variant="ghost"
      fontSize="sm"
      py="2px"
      px="8px"
      onClick={onClick}
      {...buttonProps}
    >
      {text ?? 'Download'}
    </Button>
  );
};

type DownloadMenuZipFilesProps = ButtonProps & {
  fileName: string;
  receiptId: string;
  canDownloadZipFiles: ReceiptZipFileDownloadAvailableGQLType;
};
export const DownloadMenuZipFiles = ({
  fileName: inputFileName,
  receiptId,
  canDownloadZipFiles,
  ...buttonProps
}: DownloadMenuZipFilesProps) => {
  const toast = useToast();

  const [isInput, setIsInput] = useState<boolean>(false);

  const [
    deliverablesContainerReceiptsZipFilesDownloadUrl,
  ] = useGetDeliverablesContainerReceiptZipFilesDownloadUrlLazyQueryGQLType({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
    onCompleted: (response) => {
      if (
        response &&
        response.deliverablesContainerReceiptZipFilesDownloadUrl
      ) {
        const fileName = isInput
          ? `${inputFileName}-inputs.zip`
          : `${inputFileName}.zip`;
        const url = response.deliverablesContainerReceiptZipFilesDownloadUrl;
        axios({
          url,
          method: 'GET',
          responseType: 'blob',
        })
          .then((res) => {
            const fileBlob = new Blob([res.data], {
              type: res.data.type,
            });
            FileSaver.saveAs(fileBlob, fileName);
            successToastConfig.title = `Successfully downloaded ${fileName}`;
            toast(successToastConfig);
          })
          .catch(() => {
            if (!toast.isActive(errorToastId)) {
              errorToastConfig.title = `Error downloading ${fileName}`;
              toast(errorToastConfig);
            }
          });
      }
    },
  });

  const onClick = useCallback(
    (type: InputTypeGQLType) => {
      Sentry.addBreadcrumb({
        category: 'ui.click',
        message: `Clicked download for ${inputFileName} v4 deliverables ${type} zip files`,
        level: Sentry.Severity.Info,
      });
      if (type === 'input') {
        setIsInput(true);
      } else {
        setIsInput(false);
      }
      deliverablesContainerReceiptsZipFilesDownloadUrl({
        variables: {
          receiptId,
          type,
        },
      });
    },
    [
      inputFileName,
      deliverablesContainerReceiptsZipFilesDownloadUrl,
      receiptId,
    ],
  );

  return (
    <Menu>
      <MenuButton as={Button} {...buttonProps}>
        Download
      </MenuButton>
      <MenuList width="300px">
        {canDownloadZipFiles.canDownloadOutput && (
          <MenuItem onClick={() => onClick('output')}>Report</MenuItem>
        )}
        {canDownloadZipFiles.canDownloadInput && (
          <MenuItem onClick={() => onClick('input')}>All Inputs</MenuItem>
        )}
      </MenuList>
    </Menu>
  );
};
